import { useEffect, useRef, useState } from "react";
import { useTranslation } from "react-i18next";
import { InfoMessageType } from "src/Components/InfoBar/InfoBar";
import { IPaymentProducts } from "src/Utils/Hooks/Payments/useGetPaymentProducts";
import UpgradeSubscriptionDialog from "./UpgradeSubscriptionDialog/UpgradeSubscriptionDialog";
import { useUpgradeSubscription } from "src/Utils/Hooks/Payments/useUpgradeSubscription";
import UpgradeInfo from "./UpgradeInfo/UpgradeInfo";
import UpgradeButton from "./UpgradeButton/UpgradeButton";
import PermanentUpgradeInfo from "./UpgradeInfo/PermanentUpgradeInfo";

interface IUpgradeSubscription {
    clinicId: string;
    setInfo: (message: string, type: InfoMessageType) => void;
    updatePageLoader: (load: boolean) => void;
    isUpgradeDisabled: boolean;
    products: IPaymentProducts;
    refreshProducts: (clinicId: string) => void;
    isPermanent : boolean;
}

const UpgradeSubscription: React.FC<IUpgradeSubscription> = ({ clinicId, setInfo, updatePageLoader, isUpgradeDisabled, products, refreshProducts, isPermanent }) => {
    const { t } = useTranslation();
    const [showUpgradeDialog, setShowUpgradeDialog] = useState<boolean>(false);
    const [showUpgradeInfo, setShowUpgradeInfo] = useState<boolean>(false);
    const [showPermanentUpgradeInfo, setShowPermanentUpgradeInfo] = useState<boolean>(false);
    const upgradedPackageRef = useRef<string>('');
    const upgradePriceIdRef = useRef<string>('');

    const { upgradeResponse, reqestError: upgradeError, reqestLoading: upgradeLoading, upgradeSubscription } = useUpgradeSubscription();

    useEffect(() => {
        updatePageLoader(upgradeLoading); // Show loader during any API request
    }, [upgradeLoading, updatePageLoader]);

    useEffect(() => {
        if (upgradeError) {
            setInfo(upgradeError, InfoMessageType.error);
        }
    }, [upgradeError, setInfo]);

    useEffect(() => {

        if (upgradeResponse.upgradeSuccess) {
            updatePageLoader(true);
            setTimeout(() => {
                updatePageLoader(false);
                setInfo(`Successfully upgraded to ${upgradedPackageRef.current}`, InfoMessageType.success);
                refreshProducts(clinicId);  // Refresh products after 3 seconds
            }, 3000);
            // Refresh products after upgrade
        } else if (upgradeResponse.hostedInvoiceUrl) {
            window.open(upgradeResponse.hostedInvoiceUrl,'_self');
        }
    }, [upgradeResponse, setInfo, refreshProducts, upgradedPackageRef, clinicId, updatePageLoader]);

    const handleUpgrade = (priceId: string, packageDescription: string) => {
        if(isPermanent){
             upgradePriceIdRef.current = priceId;
            setShowPermanentUpgradeInfo(true)
        }
        else{
            upgradedPackageRef.current = packageDescription;
            upgradePriceIdRef.current = priceId;
            setShowUpgradeInfo(true);
        }
       
    }

    const handleConfirmUpgrade = (upcomingInvoiceId: string) => {
        upgradeSubscription(clinicId, upgradePriceIdRef.current, upcomingInvoiceId);
        setShowUpgradeInfo(false);
        setShowUpgradeDialog(false);
    };
    const handlePermanentConfirmUpgrade = () => {
            upgradeSubscription(clinicId, upgradePriceIdRef.current, 'permanent');
            setShowPermanentUpgradeInfo(false);
            setShowUpgradeDialog(false);
    };

    return (
        <>
            {
                showUpgradeInfo &&
                <UpgradeInfo
                    onCloseUpgradeInfo={() => { setShowUpgradeInfo(false) }}
                    clinicId={clinicId}
                    priceId={upgradePriceIdRef.current}
                    setInfo={setInfo}
                    onConfirmUpgrade={handleConfirmUpgrade}
                />
            }
            {
                showPermanentUpgradeInfo &&
                <PermanentUpgradeInfo
                    onClosePermanentUpgradeInfo={() => { 
                        setShowPermanentUpgradeInfo(false) }}
                    onConfirmPermanentUpgrade={handlePermanentConfirmUpgrade}
                />
            }
            {
                showUpgradeDialog &&
                products &&
                <UpgradeSubscriptionDialog
                    isPermanent= {isPermanent}
                    products={products}
                    onClose={() => { setShowUpgradeDialog(false) }}
                    onUpgrade={handleUpgrade}
                />
            }
            <UpgradeButton
                onClick={() => { setShowUpgradeDialog(true) }}
                text={t('Upgrade')}
                disable={isUpgradeDisabled}
            />
        </>
    );
}

export default UpgradeSubscription;